/* lib/reports/abcMeta.js — реестр приоритетов и статусов food cost
 * ABC-анализа v2. id ОБЯЗАНЫ байт-в-байт совпадать с бэкендом
 * (backend/reports/abc_calc.py PRIORITIES / FC_STATUSES) — это ключи
 * priority/fc_status в Report.result и фильтра таблицы по кликабельным тегам. */

import { DEFAULT_FC_SCALE, fcRangeText, fcStatusFor, getFcScale } from '../fcScale.js';

export const ABC_PRIORITY = {
  p1: {
    label: 'Приоритет 1', short: 'P1', tone: 'bad', icon: 'alert-circle',
    title: 'A по продажам и food cost > 20 % — каждый процент FC на топовых продажах стоит дорого',
  },
  p2: {
    label: 'Приоритет 2', short: 'P2', tone: 'bad', icon: 'alert-triangle',
    title: 'BCC/CCC (кандидаты на вывод) или food cost ≥ 30 %',
  },
  p3: {
    label: 'Приоритет 3', short: 'P3', tone: 'warn', icon: 'minus',
    title: 'Просадка по прибыли или food cost 20–30 %',
  },
  potential: {
    label: 'Потенциал', short: 'Потенциал', tone: 'info', icon: 'arrow-up-right',
    title: 'Низкий food cost и B по продажам/выручке — можно усиливать продажи',
  },
  normal: {
    label: 'Норма', short: 'Норма', tone: 'ok', icon: 'check',
    title: 'Стабильные позиции без явных проблем',
  },
  // «Экономика меню» из ТТК: себестоимость позиции неполная — ни «норма», ни
  // «потенциал» по нижней границе не видны (backend abc_analysis.run_menu_economics).
  incomplete: {
    label: 'Неполные данные', short: 'Неполные', tone: 'neutral', icon: 'minus',
    title: 'Себестоимость посчитана не полностью — приоритет по food cost не определить, пока в ТТК не заполнят цены, привязки и граммы',
  },
};
// порядок сортировки/вывода тегов (зеркало бэкенд-PRIORITIES)
export const ABC_PRIORITY_ORDER = ['p1', 'p2', 'p3', 'potential', 'normal', 'incomplete'];

export const ABC_FC_STATUS = {
  no_data: {
    label: 'нет ТТК', tone: 'neutral', icon: 'minus',
    title: 'Food cost 0 % или отсутствует — нет ТТК или некорректные данные',
  },
  good: {
    label: 'в норме', tone: 'ok', icon: 'check',
    title: 'Food cost < 20 % — в пределах нормы',
  },
  above_target: {
    label: 'выше цели', tone: 'warn', icon: 'alert-triangle',
    title: 'Food cost 20–25 % — выше целевого уровня',
  },
  critical: {
    label: 'критично', tone: 'bad', icon: 'alert-circle',
    title: 'Food cost 25–30 % — критический уровень',
  },
  urgent: {
    label: 'срочно', tone: 'bad', icon: 'alert-circle',
    title: 'Food cost ≥ 30 % — срочно оптимизировать',
  },
  // «Экономика меню» из ТТК: себестоимость блюда посчитана не полностью, FC — нижняя
  // граница; «в норме» по ней не бывает (backend abc_calc.fc_status_at_least).
  // `short` — ячейке таблицы: полная подпись распирала колонку «Статус» (QA 1740).
  incomplete: {
    label: 'неполная себестоимость', short: 'неполная', tone: 'neutral', icon: 'minus',
    title: 'Себестоимость посчитана не полностью — food cost не меньше показанного',
  },
};
export const ABC_FC_ORDER = ['urgent', 'critical', 'above_target', 'good', 'incomplete', 'no_data'];

/** Метка приоритета; подпись p1/p2/p3 — с границами шкалы (`scale`: отчёта или пространства). */
export function abcPrio(id, scale) {
  const meta = ABC_PRIORITY[id] || ABC_PRIORITY.normal;
  const s = scale || getFcScale();
  const g = String(s.good).replace('.', ','); const c = String(s.critical).replace('.', ',');
  const titles = {
    p1: `A по продажам и food cost > ${g} % — каждый процент FC на топовых продажах стоит дорого`,
    p2: `BCC/CCC (кандидаты на вывод) или food cost ≥ ${c} %`,
    p3: `Просадка по прибыли или food cost ${g}–${c} %`,
  };
  return titles[id] ? { ...meta, title: titles[id] } : meta;
}
/** Метка статуса FC; подпись (title) — с границами шкалы (`scale`: отчёта или пространства). */
export function abcFc(id, scale) {
  const meta = ABC_FC_STATUS[id] || ABC_FC_STATUS.no_data;
  const range = fcRangeText(id, scale || getFcScale());
  const tail = FC_TITLE_TAIL[id];
  return range && tail ? { ...meta, title: `Food cost ${range} — ${tail}` } : meta;
}
const FC_TITLE_TAIL = {
  good: 'в пределах нормы', above_target: 'выше целевого уровня',
  critical: 'критический уровень', urgent: 'срочно оптимизировать',
};

/* Границы класса СЛОВАМИ — «критично» само по себе не говорит, где проходит
   граница, и узнать это можно было только наведением мыши (title). На карточках
   A/B/C диапазон подписан прямо («0–70 %»), а у food cost его не было вовсе:
   пять карточек с числами без единой подписи. Строится из FC_THRESHOLDS, то есть
   расходиться со шкалой не может. */
export function fcRange(id, scale) {
  // no_data — диапазона нет, класс не про проценты
  return fcRangeText(id, scale || getFcScale());
}

// Пороговая шкала food cost, % — зеркало backend/reports/abc_calc.py
// (FC_GOOD_MAX / FC_ABOVE_MAX / FC_CRITICAL_MAX + fc_status()). Нужна фронту
// ТОЛЬКО для клиентских агрегатов (группы/итоги), где бэкенд статус не отдаёт;
// статусы позиций всегда берём готовыми из ответа бэкенда.
// Умолчание шкалы; у пространства — своя (#502, lib/fcScale.js), у сохранённого отчёта — его.
export const FC_THRESHOLDS = DEFAULT_FC_SCALE;
export function fcStatusOf(pct, scale) {
  return fcStatusFor(pct, scale || getFcScale());
}
